<script>
// List of error parser functions. Each tries to extract a message from the error object.
// The first parser to return a truthy value will be used as the error message.
const errorParsers = [
  // Standard API error (ensure it's a string, not just truthy)
  error =>
    error && error.error && typeof error.error === 'string'
      ? error.error
      : null,
  // Fallback to generic message property
  error =>
    error && error.message && typeof error.message === 'string'
      ? error.message
      : null,
  // Handle array error format (e.g. validation errors)
  error => error && error[0] && error[0].reason && error[0].reason.exception,
  // Fallback to HTTP status text
  error =>
    error && error.statusText && typeof error.statusText === 'string'
      ? error.statusText
      : null
]

export default {
  name: 'ErrorHandler',

  data() {
    return {
      dialog: {
        show: false,
        title: 'Login',
        message: null
      }
    }
  },

  methods: {
    /**
     * Handle dialog errors and display custom modal
     * @param {Object} error - Error object to handle
     * @param {Object} options - Options to modify function behaviour
     */

    handleAuthError(error, options = {}) {
      // httpService returns { success, error, data, originalError }
      if (error && error.data) {
        error = error.data
      } else if ('body' in error) {
        // Fallback for old format
        error = error.body
      }

      const errorTitle = options.title || this.$t('auth.error.title') || 'Error'

      // Check for authentication errors (security best practice)
      if (this.isAuthenticationError(error)) {
        const errorMessage =
          this.$t('auth.error.invalid_credentials') ||
          'Invalid email or password'
        this.showDialog(errorTitle, errorMessage)
        return
      }

      // Use the first parser that returns a non-empty message
      const errorMessage =
        errorParsers.reduce((msg, parser) => msg || parser(error), null) ||
        options.message ||
        this.$t('auth.error.generic') ||
        'An error occurred'

      // Clé renvoyée par l'engine (ex. dash.error.mfa_client_outdated) : traduite si elle existe
      this.showDialog(errorTitle, this.$te(errorMessage) ? this.$t(errorMessage) : errorMessage)
    },

    /**
     * Check if error is authentication related
     * @param {Object} error - Error object
     * @returns {Boolean}
     */
    isAuthenticationError(error) {
      // Check HTTP status in originalError (Response object)
      if (error && error.originalError && error.originalError.status) {
        const status = error.originalError.status
        return status === 401 || status === 403
      }

      // Fallback: Check direct status property
      if (error && error.status) {
        return error.status === 401 || error.status === 403
      }

      // Check if error message is "bad_credentials"
      if (error && error.error === 'bad_credentials') {
        return true
      }

      // Fallback: Common authentication error patterns in message
      const authErrorPatterns = [
        /bad_credentials/i,
        /invalid.*credentials/i,
        /wrong.*password/i,
        /incorrect.*password/i,
        /user.*not.*found/i,
        /authentication.*failed/i,
        /login.*failed/i,
        /unauthorized/i,
        /forbidden/i
      ]

      const errorStr = JSON.stringify(error).toLowerCase()
      return authErrorPatterns.some(pattern => pattern.test(errorStr))
    },

    /**
     * Show dialog with title and message
     * @param {String} title - Dialog title
     * @param {String} message - Dialog message
     */
    showDialog(title, message) {
      this.dialog = {
        show: true,
        title: title,
        message: message
      }
    },

    /**
     * Hide dialog
     */
    hideDialog() {
      this.dialog.show = false
    },

    /**
     * Show success dialog
     * @param {String} title - Dialog title
     * @param {String} message - Dialog message
     */
    showSuccessDialog(title, message) {
      this.showDialog(title, message)
    },

    /**
     * Show error dialog
     * @param {String} message - Error message
     */
    showErrorDialog(message) {
      this.showDialog(this.$t('auth.error.title') || 'Error', message)
    }
  }
}
</script>
